import { useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
  flexRender,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import "dayjs/locale/id";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { getAuthUser } from "../../../services/auth";
import "./DBLead.css";
import Swal from "sweetalert2";
dayjs.locale("id");

export default function LeadTable({
  leads,
  onEdit,
  onDelete,
  tableLoading,
  editLoading,
  page,
  perPageSize,
  activeFilterMo,
}) {
  const columnHelper = createColumnHelper();
  const auth = getAuthUser();

  const columns = [
    columnHelper.display({
      id: "no",
      header: "No",
      size: 50,
      cell: ({ row, table }) => {
        const pageIndex = table.options.meta?.page || 1;
        const perPageSize = table.options.meta?.perPageSize || 10;
        const no = row.index + 1 + (pageIndex - 1) * perPageSize;
        return <div className="text-center">{no}</div>;
      },
    }),
    columnHelper.accessor((row) => row.created_at, {
      header: "Tanggal",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div className="text-wrap text-break" style={{ maxWidth: "200px" }}>
            {value}
          </div>
        );
      },
    }),

    columnHelper.accessor("name", {
      header: "Nama",
      size: 200,
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div className="text-wrap text-break" style={{ maxWidth: "200px" }}>
            {value}
          </div>
        );
      },
    }),

    columnHelper.accessor("phone_number", {
      header: "No Telp",
      cell: ({ getValue }) => {
        const encodedPhone = getValue();

        if (!encodedPhone || encodedPhone.trim() === "") {
          return (
            <div className="d-flex justify-content-center align-items-center text-muted">
              -
            </div>
          );
        }

        let decodedPhone = "";
        try {
          decodedPhone = atob(encodedPhone);
        } catch (err) {
          return <span className="text-danger">Nomor WA tidak valid</span>;
        }

        const formattedPhone = decodedPhone.replace(/\D/g, "");
        const isMobile = formattedPhone.startsWith("8");

        const handleClick = (e) => {
          e.preventDefault();

          if (isMobile) {
            const waLink = `https://wa.me/62${formattedPhone}`;
            window.open(waLink, "_blank", "noopener,noreferrer");
          } else {
            navigator.clipboard.writeText(decodedPhone);
            Swal.fire({
              title: `Nomor telpon rumah disalin: ${decodedPhone}`,
              icon: "success",
              draggable: true,
            });
          }
        };

        return (
          <button
            onClick={handleClick}
            className="btn btn-link text-success d-flex align-items-center justify-content-center gap-1 text-decoration-none"
          >
            {isMobile ? (
              <>
                <i className="fa-brands fa-whatsapp fs-3"></i>
                <span>Whatsapp</span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-phone fs-3"></i>
                <span>Telp Rumah</span>
              </>
            )}
          </button>
        );
      },
    }),

    columnHelper.accessor((row) => row.email, {
      header: "Email",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {value}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.organization, {
      header: "Instansi/Business",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div className="text-wrap text-break" style={{ maxWidth: "200px" }}>
            {value}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.product_needs, {
      header: "Kebutuhan Product",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {value}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.product_category?.name, {
      header: "Kategory Product",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {value}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.product_subcategory?.name, {
      header: "Sub Kategory Product",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {value}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.product_type?.name, {
      header: "Tipe Product",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {value}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.organization_type, {
      header: "Pemerintah/Swasta",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {value}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.lead_source, {
      header: "Sumber Leads",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {value}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.company, {
      header: "Perusahaan",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {value}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.mo_id, {
      header: "MO",
      cell: (info) => {
        const values = info.getValue() || [];
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {values.length === 0 && "-"}
            {values.length > 0 &&
              values.map((val) => (
                <ul
                  key={val.id}
                  style={{ marginBottom: "0px", marginTop: "0px" }}
                >
                  <li>{val.name}</li>
                </ul>
              ))}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.tls_id, {
      header: "TLS",
      cell: (info) => {
        const values = info.getValue() || [];
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {values.length === 0 && "-"}
            {values.length > 0 &&
              values.map((val, i) => (
                <ul
                  key={val.id}
                  style={{ marginBottom: "0px", marginTop: "0px" }}
                >
                  <li>{val.name}</li>
                </ul>
              ))}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.sales_id, {
      header: "Sales",
      cell: (info) => {
        const values = info.getValue() || [];
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {values.length === 0 && "-"}
            {values.length > 0 &&
              values.map((val, i) => (
                <ul
                  key={val.id}
                  style={{ marginBottom: "0px", marginTop: "0px" }}
                >
                  <li>{val.name}</li>
                </ul>
              ))}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.lead_status, {
      header: "Status Leads",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {value === "Cold" ? (
              <div className="text-primary fw-bold">{value}</div>
            ) : value === "Warm" ? (
              <div className="text-warning fw-bold">{value}</div>
            ) : value === "Hot" ? (
              <div className="text-danger fw-bold">{value}</div>
            ) : (
              <div className="text-secondary fw-bold">-</div>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.spk_status, {
      header: "Proses",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {value === "SPK" ? (
              <div className="badge  bg-success">{value}</div>
            ) : value === "On Progress" ? (
              <div className="badge bg-primary">{value}</div>
            ) : value === "Tidak SPK" ? (
              <div className="badge bg-danger">{value}</div>
            ) : (
              <div className="badge bg-secondary">{value}</div>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.click_plan, {
      header: "Rencana Klik",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {value}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.budget, {
      header: "Pagu",
      cell: (info) => {
        const rawValue = info.getValue() || "-";
        const value =
          rawValue && !isNaN(rawValue)
            ? new Intl.NumberFormat("id-ID", {
                style: "currency",
                currency: "IDR",
                minimumFractionDigits: 0,
              }).format(rawValue)
            : "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {value}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.estimated_budget, {
      header: "RAB",
      cell: (info) => {
        const rawValue = info.getValue() || "-";
        const value =
          rawValue && !isNaN(rawValue)
            ? new Intl.NumberFormat("id-ID", {
                style: "currency",
                currency: "IDR",
                minimumFractionDigits: 0,
              }).format(rawValue)
            : "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {value}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.contract_value, {
      header: "Nominal SPK",
      cell: (info) => {
        const rawValue = info.getValue() || "-";
        const value =
          rawValue && !isNaN(rawValue)
            ? new Intl.NumberFormat("id-ID", {
                style: "currency",
                currency: "IDR",
                minimumFractionDigits: 0,
              }).format(rawValue)
            : "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {value}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.profit, {
      header: "Profit",
      cell: (info) => {
        const value = info.getValue() + "%";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {value}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.notes, {
      header: "Keterangan",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {value}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.response_status, {
      header: "Respon",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {value == "Respon" ? (
              <div className="badge text-white bg-primary">{value}</div>
            ) : (
              <div className="badge text-white bg-warning">{value}</div>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.created_by?.name, {
      header: "Dibuat Oleh",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {value}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.updated_by?.name, {
      header: "Diupdate Oleh",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {value}
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "Aksi",
      cell: ({ row }) => {
        const item = row.original;

        const sameMo = auth?.mo?.mo_id === activeFilterMo;
        const isMo = auth?.role === "mo";
        const isTls = auth?.role === "tls";
        const isSales = auth?.role === "sales";
        const isOwner = auth?.id === item?.created_by?.id;

        const canEdit =
          auth?.role === "admin" ||
          auth?.role === "super-user" ||
          (isMo && sameMo) ||
          (isTls && sameMo) ||
          (isSales && sameMo && isOwner);

        return (
          <div className="btn-action-dbLead d-flex gap-1 justify-content-center flex-md-row flex-column">
            {canEdit ? (
              <>
                <button
                  className="btn btn-sm btn-warning"
                  onClick={() => onEdit(item)}
                  disabled={editLoading}
                >
                  {editLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Loading...
                    </>
                  ) : (
                    "Edit"
                  )}
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => onDelete(item.id)}
                >
                  Hapus
                </button>
              </>
            ) : (
              <i className="fa fa-lock fs-4"></i>
            )}
          </div>
        );
      },
      meta: {
        isSticky: true,
        className: "size-column-dbLead",
      },
    }),
  ].filter(Boolean);

  const table = useReactTable({
    data: leads,
    columns,
    meta: {
      page,
      perPageSize,
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: Math.ceil(leads.length / 10),
  });

  return (
    <>
      {tableLoading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-success" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div style={{ maxHeight: "543px", overflowY: "auto" }}>
          <table className="table table-bordered table-striped">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={header.column.columnDef.meta?.className || ""}
                      style={{
                        width: header.getSize(),
                        textAlign: "center",
                        position: "sticky",
                        top: 0,
                        background: "white",
                        zIndex: 2,
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows?.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                          minWidth: cell.column.getSize(),
                          maxHeight: "60px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          position: cell.column.columnDef.meta?.isSticky
                            ? "sticky"
                            : undefined,
                          right: cell.column.columnDef.meta?.isSticky
                            ? 0
                            : undefined,
                          background: "white",
                          zIndex: 1,
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="text-center py-3">
                    Tidak ada data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
